import React, { useCallback, useEffect, useState } from "react";
import {
  gxcreatesecurityquestionsApi,
  gxgetsecurityquestionsApi,
  gxupdatesecurityquestionsApi,
} from "@/app/apiConfig/services/customerAbstractApiLayer";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";
import SecuritySelectDropdown from "@/app/components/common/actions/dropdowns/securitySelectDropdown";
import DialogTechnical from "@/app/models/dialogs/TechnicalError/DialogTechnical";
import TextAreaInput from "@/app/components/common/actions/inputs-b/TextAreaInput";

interface Props {
  setsecurityQuestions: React.Dispatch<React.SetStateAction<() => void>>;
  onclose: React.Dispatch<React.SetStateAction<boolean>>;
}

interface SecurityQuestionOption {
  value: number;
  text: string;
}

const SecurityQuestions = ({
  setsecurityQuestions,
  onclose,
}: Readonly<Props>) => {
  const [question1, setQuestion1] = useState<number | null>(null);
  const [answer1, setAnswer1] = useState<string>("");
  const [question2, setQuestion2] = useState<number | null>(null);
  const [answer2, setAnswer2] = useState<string>("");

  const [isErrorOpen, setErrorOpen] = useState(false);
  const userData = useSelector((state: RootState) => state.userData.data);
  const [isEditMode, setIsEditMode] = useState(false);
  const [securityQuestionsApi, setsecurityQuestionsApi] = useState<
    SecurityQuestionOption[]
  >([]);

  const handleSubmit = useCallback(async () => {
    try {
      let response;

      if (isEditMode) {
        const updatePayload = [
          {
            userId: userData.userId,
            secQuestId: question1,
            answer: answer1.trim(),
          },
          {
            userId: userData.userId,
            secQuestId: question2,
            answer: answer2.trim(),
          },
        ];

        response = await gxupdatesecurityquestionsApi(updatePayload);
      } else {
        const createPayload = [
          {
            userId: userData.userId,
            secQuestId: null,
            answer: "",
          },
          {
            userId: userData.userId,
            secQuestId: null,
            answer: "",
          },
        ];

        response = await gxcreatesecurityquestionsApi(createPayload);
      }

      if (response?.status === 1) {
        onclose(false);
      } else {
        setErrorOpen(true);
      }
    } catch (error) {
      console.error("Error saving security questions:", error);
      setErrorOpen(true);
    }
  }, [
    isEditMode,
    question1,
    question2,
    answer1,
    answer2,
    userData?.userId,
    onclose,
  ]);

  const getsecurityquestionsApiData = async (userId: any) => {
    const Payload = { userId };

    try {
      const response = await gxgetsecurityquestionsApi(Payload);

      if (response?.data) {
        const mappedData = response.data.map((q: any) => ({
          value: q.secQuestId,
          text: q.secQuestName,
        }));

        setsecurityQuestionsApi(mappedData);
        const selected = response.data
          .filter((q: any) => q.checkbox === "Y")
          .sort((a: any, b: any) => (a.questionNo ?? 0) - (b.questionNo ?? 0));

        const hasExisting = selected.length > 0;
        setIsEditMode(hasExisting);
        setQuestion1(null);
        setQuestion2(null);
        setAnswer1("");
        setAnswer2("");

        if (selected[0]) {
          setQuestion1(selected[0].secQuestId);
          setAnswer1(selected[0].answer || "");
        }

        if (selected[1]) {
          setQuestion2(selected[1].secQuestId);
          setAnswer2(selected[1].answer || "");
        }
      }
    } catch (error) {
      console.error("Error fetching security questions:", error);
    }
  };

  useEffect(() => {
    getsecurityquestionsApiData(userData?.userId);
  }, [userData?.userId]);

  useEffect(() => {
    setsecurityQuestions(() => handleSubmit);
  }, [handleSubmit, setsecurityQuestions]);

  const question1Options = securityQuestionsApi.filter(
    (item) => item.value !== question2,
  );

  const question2Options = securityQuestionsApi.filter(
    (item) => item.value !== question1,
  );

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="grid grid-cols-12 gap-4"
      >
        <div className="col-span-12">
          <p className="mb-3">Security Question 1</p>

          <SecuritySelectDropdown
            placeholder="Select a question"
            value={question1}
            onChange={(val) => setQuestion1(Number(val))}
            data={question1Options}
          />
        </div>

        <div className="col-span-12">
          <TextAreaInput
            value={answer1}
            rows={3}
            label="Answer"
            onChange={(e) => setAnswer1(e.target.value)}
          />
        </div>

        <div className="col-span-12">
          <p className="mb-3">Security Question 2</p>

          <SecuritySelectDropdown
            placeholder="Select a question"
            value={question2}
            onChange={(val) => setQuestion2(Number(val))}
            data={question2Options}
          />
        </div>

        <div className="col-span-12">
          <TextAreaInput
            value={answer2}
            rows={3}
            label="Answer"
            onChange={(e) => setAnswer2(e.target.value)}
          />
        </div>
      </form>

      <DialogTechnical open={isErrorOpen} close={() => setErrorOpen(false)} />
    </>
  );
};

export default SecurityQuestions;
